// Экран администрирования (SPEC §4.1 /admin, FR-ADMIN). Только accountRole==='admin' (гейт в роутере
// + здесь повторно). Очередь заявок (adminPending/approve), пользователи (модалка «Редактировать»:
// профиль/роль/активность/сброс пароля + удаление), аудит (adminAudit, пагинация). Сетевое — через
// transport.rest (админ-канал, §4.0).

import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import type { AccountRole, AuditEntry, SelfUser, UserId } from '@tele-chat/shared-contracts'
import { Button, IconButton, Avatar, Modal } from '@tele-chat/design-system'
import { useRest } from '../transport/TransportContext.js'
import { useSelf } from '../auth/SessionContext.js'
import { useNotifications } from '../notifications/NotificationsContext.js'
import { EmptyState } from '../components/EmptyState.js'
import { strings } from '../i18n/strings.js'

type Tab = 'pending' | 'users' | 'audit'

const section: CSSProperties = { padding: 'var(--space-4)' }
const userRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: 'var(--space-2) 0',
  borderBottom: '1px solid var(--color-divider)',
}
/** Инпут формы модалки (единый вид с inline-полями экрана). */
const inputStyle: CSSProperties = {
  width: '100%',
  minWidth: 0,
  padding: '8px',
  borderRadius: 'var(--radius-control)',
  border: '1px solid var(--color-divider)',
  background: 'var(--color-surface)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-sm)',
  boxSizing: 'border-box',
}

export function AdminScreen(): JSX.Element {
  const rest = useRest()
  const self = useSelf()
  const navigate = useNavigate()
  const { notify } = useNotifications()

  const [tab, setTab] = useState<Tab>('pending')
  const [pending, setPending] = useState<SelfUser[]>([])
  const [users, setUsers] = useState<SelfUser[]>([])
  const [deleteFor, setDeleteFor] = useState<SelfUser | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  // Модалка «Параметры пользователя»: снимок цели + правимые поля формы.
  const [editFor, setEditFor] = useState<SelfUser | null>(null)
  const [editName, setEditName] = useState('')
  const [editTitle, setEditTitle] = useState('')
  const [editDept, setEditDept] = useState('')
  const [editRole, setEditRole] = useState<AccountRole>('member')
  const [editActive, setEditActive] = useState(true)
  const [editPw, setEditPw] = useState('')
  const [editBusy, setEditBusy] = useState(false)
  const [audit, setAudit] = useState<AuditEntry[]>([])
  const [auditPage, setAuditPage] = useState(0)
  const [auditHasMore, setAuditHasMore] = useState(false)

  const reloadPending = useCallback(async (): Promise<void> => {
    try {
      setPending(await rest.adminPending())
    } catch {
      setPending([])
    }
  }, [rest])

  const reloadUsers = useCallback(async (): Promise<void> => {
    try {
      setUsers(await rest.adminListUsers())
    } catch {
      setUsers([])
    }
  }, [rest])

  const loadAudit = useCallback(
    async (page: number): Promise<void> => {
      try {
        const r = await rest.adminAudit(page)
        setAudit((prev) => (page === 0 ? r.entries : [...prev, ...r.entries]))
        setAuditHasMore(r.hasMore)
        setAuditPage(page)
      } catch {
        if (page === 0) setAudit([])
      }
    },
    [rest],
  )

  useEffect(() => {
    void reloadPending()
  }, [reloadPending])

  useEffect(() => {
    if (tab === 'audit' && audit.length === 0) void loadAudit(0)
  }, [tab, audit.length, loadAudit])

  useEffect(() => {
    if (tab === 'users') void reloadUsers()
  }, [tab, reloadUsers])

  const onError = useCallback(() => notify({ kind: 'error', title: strings.errors.actionFailed }), [notify])

  const approve = useCallback(
    async (id: UserId): Promise<void> => {
      try {
        await rest.adminApprove(id)
        setPending((list) => list.filter((u) => u.id !== id))
        setUsers((list) => list.map((u) => (u.id === id ? { ...u, isActive: true } : u)))
      } catch {
        onError()
      }
    },
    [rest, onError],
  )

  const deactivate = useCallback(
    async (id: UserId): Promise<void> => {
      try {
        await rest.adminDeactivate(id)
        setPending((list) => list.filter((u) => u.id !== id))
        setUsers((list) => list.map((u) => (u.id === id ? { ...u, isActive: false } : u)))
      } catch {
        onError()
      }
    },
    [rest, onError],
  )

  const confirmDelete = useCallback(async (): Promise<void> => {
    if (deleteFor === null) return
    const id = deleteFor.id
    setDeleteBusy(true)
    try {
      await rest.adminDeleteUser(id)
      setUsers((list) => list.filter((u) => u.id !== id))
      setPending((list) => list.filter((u) => u.id !== id))
      setDeleteFor(null)
      notify({ kind: 'info', title: strings.admin.userDeleted })
    } catch {
      onError()
    } finally {
      setDeleteBusy(false)
    }
  }, [deleteFor, rest, notify, onError])

  const openEdit = useCallback((u: SelfUser): void => {
    setEditFor(u)
    setEditName(u.name)
    setEditTitle(u.title ?? '')
    setEditDept(u.department ?? '')
    setEditRole(u.accountRole)
    setEditActive(u.isActive)
    setEditPw('')
  }, [])

  /**
   * Сохранение модалки: применяет ТОЛЬКО изменённые параметры, в безопасном порядке —
   * активация → профиль (правится только у активного) → роль → пароль → деактивация последней.
   */
  const saveEdit = useCallback(async (): Promise<void> => {
    if (editFor === null) return
    const id = editFor.id
    const name = editName.trim()
    setEditBusy(true)
    try {
      let updated: SelfUser = editFor

      // 1. Активация — до правки профиля (профиль деактивированного сервер не правит).
      if (editActive && !editFor.isActive) {
        await rest.adminApprove(id)
        updated = { ...updated, isActive: true }
      }
      // 2. Профильные поля — только у активного; пустая должность/отдел = очистка.
      if (updated.isActive) {
        const patch: { name?: string; title?: string; department?: string } = {}
        if (name.length > 0 && name !== editFor.name) patch.name = name
        if (editTitle.trim() !== (editFor.title ?? '')) patch.title = editTitle.trim()
        if (editDept.trim() !== (editFor.department ?? '')) patch.department = editDept.trim()
        if (Object.keys(patch).length > 0) {
          const fresh = await rest.adminUpdateUser(id, patch)
          updated = { ...updated, name: fresh.name, title: fresh.title, department: fresh.department }
        }
      }
      // 3. Роль (защита последнего админа — на сервере, CONFLICT ниже).
      if (editRole !== editFor.accountRole) {
        await rest.adminSetRole(id, editRole)
        updated = { ...updated, accountRole: editRole }
      }
      // 4. Пароль (опционально): сервер отзывает все сессии пользователя — форс-релогин.
      if (editPw.length > 0) {
        await rest.adminResetPassword(id, editPw)
      }
      // 5. Деактивация — последним шагом (до неё профиль ещё был правим).
      if (!editActive && editFor.isActive) {
        await rest.adminDeactivate(id)
        updated = { ...updated, isActive: false }
      }

      setUsers((list) => list.map((u) => (u.id === id ? updated : u)))
      void reloadPending() // активация/деактивация меняет очередь заявок
      setEditFor(null)
      notify({ kind: 'info', title: strings.settings.saved })
    } catch (err) {
      // CONFLICT = защита последнего активного админа (deactivate/demote) — говорим прямо.
      if ((err as { code?: unknown }).code === 'CONFLICT') {
        notify({ kind: 'error', title: strings.admin.lastAdminConflict })
      } else {
        onError()
      }
    } finally {
      setEditBusy(false)
    }
  }, [editFor, editName, editTitle, editDept, editRole, editActive, editPw, rest, reloadPending, notify, onError])

  if (self?.accountRole !== 'admin') {
    return <EmptyState title={strings.errors.generic} />
  }

  const header: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-2) var(--space-3)',
    borderBottom: '1px solid var(--color-divider)',
    background: 'var(--color-surface)',
  }
  const tabs: CSSProperties = {
    display: 'flex',
    gap: 'var(--space-1)',
    padding: 'var(--space-2) var(--space-3)',
    borderBottom: '1px solid var(--color-divider)',
  }

  return (
    <div data-screen="admin" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={header}>
        <IconButton icon="back" label={strings.nav.back} onClick={() => navigate('/settings')} />
        <span style={{ flex: 1, fontSize: 'var(--font-lg)', fontWeight: 'var(--weight-semibold)' }}>
          {strings.nav.admin}
        </span>
      </div>

      <div style={tabs}>
        <TabButton active={tab === 'pending'} onClick={() => setTab('pending')} label={strings.admin.pending} count={pending.length} />
        <TabButton active={tab === 'users'} onClick={() => setTab('users')} label={strings.admin.users} />
        <TabButton active={tab === 'audit'} onClick={() => setTab('audit')} label={strings.admin.audit} />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {tab === 'pending' ? (
          pending.length === 0 ? (
            <EmptyState title={strings.empty.adminQueueEmpty} />
          ) : (
            <div style={section} data-admin-pending="">
              {pending.map((u) => (
                <div key={u.id} data-pending-user={u.id} style={userRow}>
                  <Avatar name={u.name} src={u.avatarUrl} size={40} colorKey={u.id} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontWeight: 'var(--weight-medium)' }}>{u.name}</span>
                    <span style={{ display: 'block', fontSize: 'var(--font-xs)', color: 'var(--color-text-muted)' }}>
                      {u.email}
                    </span>
                  </span>
                  <Button variant="primary" data-approve="" onClick={() => void approve(u.id)}>
                    {strings.admin.approve}
                  </Button>
                  <Button variant="ghost" data-reject="" onClick={() => void deactivate(u.id)}>
                    {strings.admin.deactivate}
                  </Button>
                </div>
              ))}
            </div>
          )
        ) : null}

        {tab === 'users' ? (
          users.length === 0 ? (
            <EmptyState title={strings.empty.noUsers} />
          ) : (
            <div style={section} data-admin-users="">
              {users.map((u) => (
                <div key={u.id} data-admin-user={u.id} style={{ ...userRow, flexWrap: 'wrap' }}>
                  <Avatar name={u.name} src={u.avatarUrl} size={40} colorKey={u.id} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontWeight: 'var(--weight-medium)' }}>
                      {u.name}
                      {u.id === self?.id ? ` ${strings.admin.you}` : ''}
                    </span>
                    <span style={{ display: 'block', fontSize: 'var(--font-xs)', color: 'var(--color-text-muted)' }}>
                      {u.email}
                    </span>
                    <span style={{ display: 'flex', gap: '6px', marginTop: '2px' }}>
                      <StatusBadge
                        text={u.accountRole === 'admin' ? strings.admin.roleAdmin : strings.admin.roleMember}
                        tone={u.accountRole === 'admin' ? 'accent' : 'muted'}
                      />
                      <StatusBadge
                        text={u.isActive ? strings.admin.active : strings.admin.pendingBadge}
                        tone={u.isActive ? 'ok' : 'warn'}
                      />
                    </span>
                  </span>
                  <span style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
                    {!u.isActive ? (
                      <Button variant="primary" data-activate="" onClick={() => void approve(u.id)}>
                        {strings.admin.approve}
                      </Button>
                    ) : null}
                    <Button variant="ghost" data-edit-user="" onClick={() => openEdit(u)}>
                      {strings.admin.edit}
                    </Button>
                    {u.id !== self?.id ? (
                      <Button variant="danger" data-delete-user="" onClick={() => setDeleteFor(u)}>
                        {strings.admin.deleteUser}
                      </Button>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          )
        ) : null}

        {tab === 'audit' ? (
          <div style={section} data-admin-audit="">
            {audit.map((e) => (
              <div key={e.id} data-audit-entry={e.id} style={{ ...userRow, gap: 'var(--space-2)' }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--font-sm)' }}>
                  <strong>{e.action}</strong>
                  <span style={{ color: 'var(--color-text-muted)' }}> — {e.at}</span>
                </span>
              </div>
            ))}
            {auditHasMore ? (
              <Button variant="secondary" onClick={() => void loadAudit(auditPage + 1)} style={{ marginTop: 'var(--space-2)' }}>
                {strings.app.loading}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <Modal
        open={editFor !== null}
        onClose={() => (editBusy ? undefined : setEditFor(null))}
        label={strings.admin.editTitle}
      >
        <div
          data-edit-user-modal=""
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', width: 'min(420px, 82vw)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <Avatar name={editFor?.name ?? ''} src={editFor?.avatarUrl} size={40} colorKey={editFor?.id ?? ''} />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: 'var(--weight-semibold)' }}>{strings.admin.editTitle}</span>
              <span style={{ display: 'block', fontSize: 'var(--font-xs)', color: 'var(--color-text-muted)' }}>
                {editFor?.email}
              </span>
            </span>
          </div>

          <FieldRow label={strings.auth.name}>
            <input
              data-edit-name=""
              type="text"
              value={editName}
              disabled={!editActive}
              onChange={(e) => setEditName(e.target.value)}
              style={inputStyle}
            />
          </FieldRow>
          <FieldRow label={strings.auth.title}>
            <input
              data-edit-title=""
              type="text"
              value={editTitle}
              disabled={!editActive}
              onChange={(e) => setEditTitle(e.target.value)}
              style={inputStyle}
            />
          </FieldRow>
          <FieldRow label={strings.auth.department}>
            <input
              data-edit-department=""
              type="text"
              value={editDept}
              disabled={!editActive}
              onChange={(e) => setEditDept(e.target.value)}
              style={inputStyle}
            />
          </FieldRow>
          {!editActive ? (
            <span style={{ fontSize: 'var(--font-xs)', color: 'var(--color-text-muted)' }}>
              {strings.admin.editInactiveHint}
            </span>
          ) : null}

          <FieldRow label={strings.admin.editRole}>
            <select
              data-edit-role=""
              value={editRole}
              onChange={(e) => setEditRole(e.target.value === 'admin' ? 'admin' : 'member')}
              style={{ ...inputStyle, appearance: 'auto' }}
            >
              <option value="member">{strings.admin.roleMember}</option>
              <option value="admin">{strings.admin.roleAdmin}</option>
            </select>
          </FieldRow>

          <label
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer', fontSize: 'var(--font-sm)' }}
          >
            <input
              data-edit-active=""
              type="checkbox"
              checked={editActive}
              onChange={(e) => setEditActive(e.target.checked)}
            />
            {strings.admin.editActive}
          </label>

          <FieldRow label={strings.settings.newPassword} hint={strings.admin.editPasswordHint}>
            <input
              data-edit-password=""
              type="password"
              autoComplete="new-password"
              value={editPw}
              onChange={(e) => setEditPw(e.target.value)}
              style={inputStyle}
            />
          </FieldRow>

          <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setEditFor(null)} disabled={editBusy}>
              {strings.admin.editCancel}
            </Button>
            <Button
              variant="primary"
              data-edit-save=""
              disabled={editBusy || (editActive && editName.trim().length === 0)}
              onClick={() => void saveEdit()}
            >
              {strings.settings.save}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={deleteFor !== null} onClose={() => setDeleteFor(null)} label={strings.admin.deleteConfirmTitle}>
        <div data-delete-confirm="" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', maxWidth: '360px' }}>
          <h3 style={{ margin: 0 }}>{strings.admin.deleteConfirmTitle}</h3>
          <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: 'var(--font-sm)' }}>
            {strings.admin.deleteConfirmBody(deleteFor?.name ?? '')}
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setDeleteFor(null)} disabled={deleteBusy}>
              {strings.admin.deleteConfirmNo}
            </Button>
            <Button variant="danger" data-delete-confirm-yes="" disabled={deleteBusy} onClick={() => void confirmDelete()}>
              {strings.admin.deleteConfirmYes}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

/** Подписанное поле формы модалки «Параметры пользователя». */
function FieldRow({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: ReactNode
}): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <span style={{ fontSize: 'var(--font-xs)', color: 'var(--color-text-muted)' }}>{label}</span>
      {children}
      {hint !== undefined ? (
        <span style={{ fontSize: 'var(--font-xs)', color: 'var(--color-text-muted)' }}>{hint}</span>
      ) : null}
    </label>
  )
}

/** Мелкий бейдж статуса/роли пользователя (админ-список). */
function StatusBadge({ text, tone }: { text: string; tone: 'accent' | 'muted' | 'ok' | 'warn' }): JSX.Element {
  const colors: Record<string, { bg: string; fg: string }> = {
    accent: { bg: 'var(--color-accent-muted)', fg: 'var(--color-accent)' },
    muted: { bg: 'var(--color-surface-hover)', fg: 'var(--color-text-muted)' },
    ok: { bg: 'var(--color-accent-muted)', fg: 'var(--color-online)' },
    warn: { bg: 'var(--color-surface-hover)', fg: 'var(--color-danger)' },
  }
  const c = colors[tone] ?? colors['muted']!
  return (
    <span
      style={{
        fontSize: 'var(--font-xs)',
        padding: '1px 8px',
        borderRadius: 'var(--radius-pill)',
        background: c.bg,
        color: c.fg,
      }}
    >
      {text}
    </span>
  )
}

function TabButton({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean
  onClick: () => void
  label: string
  count?: number
}): JSX.Element {
  return (
    <button
      type="button"
      data-admin-tab={label}
      data-active={active ? '' : undefined}
      onClick={onClick}
      style={{
        border: 'none',
        background: active ? 'var(--color-accent-muted)' : 'transparent',
        color: active ? 'var(--color-accent)' : 'var(--color-text)',
        borderRadius: 'var(--radius-control)',
        padding: 'var(--space-2) var(--space-3)',
        cursor: 'pointer',
        fontWeight: 'var(--weight-medium)',
      }}
    >
      {label}
      {count !== undefined && count > 0 ? ` (${count})` : ''}
    </button>
  )
}
